import autoBindMethods from 'class-autobind-decorator';

import Client from './Client';

interface IUploadUrlResponse {
  file_field: string;
  file_path: string;
  upload_data: { [key: string]: string };
  upload_url: string;
}

/**
 * A client for an endpoint backed by the API's `DirectFileTransferMixin`, where a file goes to
 * storage without passing through the web server. Creating a record is three requests rather than
 * one multipart POST:
 *
 *   1. ask `upload-url/` where to put the file, and for the presigned form fields to put it with
 *   2. POST the file straight to storage
 *   3. create the record naming the path written to in step 2
 *
 * The API validates that something is actually at that path, so a create can't outlive an upload
 * that failed halfway.
 */
@autoBindMethods
class ClientDirectUpload extends Client {
  public async getUploadUrl (filename: string, fileField: string): Promise<IUploadUrlResponse> {
    return (await this.createAction('upload-url', { file_field: fileField, filename }));
  }

  public async createWithFile (file: File, data: any = {}, fileField: string = 'file') {
    const { file_field, file_path, upload_data, upload_url } = await this.getUploadUrl(file.name, fileField);

    await this.transport.postFile(upload_url, file, upload_data);

    // The field the endpoint signed for, which is where the path belongs. It echoes the request
    // today, so this only matters if that ever stops being true -- and then it is an opaque 400.
    return (await this.create({ ...data, [file_field || fileField]: file_path }));
  }
}

export default ClientDirectUpload;
